import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, BookmarkPlus, BookmarkCheck, MessageSquare, Flag, Ban } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import ReportModal from "@/components/ReportModal";

/**
 * Chevron-down dropdown of author actions shown next to a username.
 * Options: Follow / Unfollow, Message (opens a 1:1 conversation in the feed),
 * and Report (flags the content this menu is attached to — post/reply/listing).
 * Hidden on your own posts (can't follow/message yourself).
 */
export default function AuthorMenu({ userId, name = "", reportType = null, reportId = null }) {
    const { user, isFollowing, toggleFollow, isBlocked, toggleBlock } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    if (!userId) return null;
    if (user && user.id === userId) return null;

    const following = isFollowing("user", userId);
    const blocked = isBlocked(userId);
    const handle = name ? `${String(name).replace(/^@/, "")}` : "this member";

    const onFollow = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        if (!user) { toast.error("Log in to follow"); return; }
        const res = await toggleFollow("user", userId);
        if (!res.ok) { toast.error(res.error || "Could not update follow"); return; }
        toast.success(res.following ? `Following ${handle}` : `Unfollowed ${handle}`);
    };

    const onMessage = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        if (!user) { toast.error("Log in to send messages"); return; }
        navigate(`/feed?tab=messages&u=${userId}`);
    };

    const onReport = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        if (!user) { toast.error("Log in to report"); return; }
        setReportOpen(true);
    };

    const onBlock = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        if (!user) { toast.error("Log in to block"); return; }
        const res = await toggleBlock(userId);
        if (!res.ok) { toast.error(res.error || "Could not update block"); return; }
        toast.success(res.blocked ? `Blocked ${handle} — they can no longer message you` : `Unblocked ${handle}`);
    };

    return (
        <span className="relative inline-flex" ref={ref}>
            <button
                type="button"
                data-testid="author-menu-trigger"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }}
                title="More actions"
                aria-label="Author actions"
                className="h-6 w-6 inline-flex items-center justify-center text-[#8a8a8a] hover:text-white transition-colors"
            >
                <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div
                    data-testid="author-menu"
                    className="absolute right-0 top-7 z-[9999] min-w-[9rem] bg-[#141414] border border-[#2a2a2a] shadow-xl py-1"
                >
                    <button
                        type="button"
                        data-testid="author-menu-follow"
                        onClick={onFollow}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-white hover:bg-[#1a1a1a]"
                    >
                        {following ? <BookmarkCheck className="h-4 w-4 text-[#22c55e]" /> : <BookmarkPlus className="h-4 w-4 text-[#a0a0a0]" />}
                        {following ? "Unfollow" : "Follow"}
                    </button>
                    {!blocked && (
                        <button
                            type="button"
                            data-testid="author-menu-message"
                            onClick={onMessage}
                            title="Send a direct message"
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-white hover:bg-[#1a1a1a]"
                        >
                            <MessageSquare className="h-4 w-4 text-[#a0a0a0]" />
                            Message
                        </button>
                    )}
                    {reportType && reportId && (
                        <button
                            type="button"
                            data-testid="author-menu-report"
                            onClick={onReport}
                            title="Report this to admins"
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-white hover:bg-[#1a1a1a]"
                        >
                            <Flag className="h-4 w-4 text-red-500" />
                            Report
                        </button>
                    )}
                    <button
                        type="button"
                        data-testid="author-menu-block"
                        onClick={onBlock}
                        title={blocked ? "Unblock this member" : "Block direct messages from this member"}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-[#1a1a1a] border-t border-[#2a2a2a] text-red-400"
                    >
                        <Ban className="h-4 w-4" />
                        {blocked ? "Unblock" : "Block"}
                    </button>
                </div>
            )}
            <ReportModal open={reportOpen} onClose={() => setReportOpen(false)} targetType={reportType} targetId={reportId} />
        </span>
    );
}
